import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Reusable Button Component
 * 
 * Variants:
 * - primary: Blue background, white text (default)
 * - secondary: Gray background, dark text
 * - destructive: Red background, white text
 * - ghost: Transparent background, colored text on hover
 * - icon: Round, icon-only
 * 
 * Sizes:
 * - sm: Small padding, smaller text
 * - md: Medium padding, normal text (default)
 * - lg: Large padding, larger text
 * - icon: Square/Circle for icons
 */
const Button = ({
    children,
    variant = 'primary',
    size = 'md',
    className = '',
    isLoading = false,
    disabled = false,
    fullWidth = false,
    onClick,
    type = 'button',
    ...props
}) => {

    const baseStyles = "inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none rounded-lg";

    const variants = {
        primary: "bg-accent-mint hover:bg-emerald-400 text-black shadow-sm focus:ring-accent-mint",
        secondary: "bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-slate-100 focus:ring-gray-500",
        destructive: "bg-red-600 hover:bg-red-700 text-white shadow-sm focus:ring-red-500",
        ghost: "bg-transparent hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-700 dark:text-slate-300",
        link: "bg-transparent text-accent-mint hover:underline p-0 h-auto",
        outline: "border border-gray-300 dark:border-zinc-700 bg-transparent hover:bg-gray-50 dark:hover:bg-zinc-800 text-gray-700 dark:text-slate-300"
    };

    const sizes = {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 py-2 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-9 w-9 p-0 rounded-full", // for icon buttons
        iconSm: "h-8 w-8 p-0 rounded-full"
    };

    const widthClass = fullWidth ? "w-full" : "";

    return (
        <button
            type={type}
            className={`
        ${baseStyles} 
        ${variants[variant] || variants.primary} 
        ${sizes[size] || sizes.md} 
        ${widthClass} 
        ${className}
      `}
            disabled={disabled || isLoading}
            onClick={onClick}
            {...props}
        >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {children}
        </button>
    );
};

export default Button;
